import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { UserService, LicenseType, RankType } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-edit-user-information',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './edit-user-information.component.html',
  styleUrl: './edit-user-information.component.scss'
})
export class EditUserInformationComponent implements OnInit, OnDestroy {
  createUserForm!: FormGroup;
  currentStep = 1;
  totalSteps = 2;
  isSubmitting = false;
  licenses: LicenseType[] = [];
  ranks: RankType[] = [];
  isLoadingData = false;
  userId: string = '';
  userCid: string = '';
  userEmail: string = '';
  courseList: any[] = [];
  isLoadingCourses = false;
  companyList: any[] = [];
  isLoadingCompanies = false;
  selectedCompany: any = null;
  isLoadingUserData = false; // Start as false, only show loading when actually fetching
  userInformationId: string = '';
  userStatusId: number | null = null; // Store user status ID
  userStatusNote: string = ''; // Store rejection reason
  showDebugModal = false; // Control debug modal visibility
  debugJsonData: string = ''; // Store formatted JSON for debug modal

  // Contact Admin Modal
  showContactModal = false;
  contactMessage = '';

  // ไฟล์แนบประกอบคำขอแก้ไขข้อมูล (ฝั่งผู้ใช้งาน)
  readonly contactAttachmentMaxSize = 25 * 1024 * 1024; // 25MB เท่ากับขีดจำกัดการแนบไฟล์ทางอีเมลตามปกติ
  readonly contactAttachmentAccept = '.jpg,.jpeg,.png,.pdf';
  private readonly contactAttachmentMimeTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf'
  ];
  private readonly contactAttachmentExtensions = /\.(jpe?g|png|pdf)$/i;
  contactAttachment: File | null = null;
  contactAttachmentUrl: string | null = null;
  contactAttachmentError = '';
  isUploadingAttachment = false;
  birthDateBE: string = '';
  maxBirthDate: string = '';

  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userService: UserService,
    private authService: AuthService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    console.log('🏗️ EditUserInformationComponent constructor called');
    this.initForm();
    this.loadUserFromLocalStorage();
    this.setMaxBirthDate();
    console.log('✅ Constructor completed, userId:', this.userId);
  }

  ngOnInit(): void {
    console.log('🔄 ngOnInit called, userId:', this.userId);

    // Only proceed if we have a valid userId
    if (!this.userId) {
      console.error('❌ No userId available in ngOnInit');
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      this.router.navigate(['/login']);
      return;
    }

    console.log('📋 Setting up component...');
    this.setupAddressWatchers();
    this.initializeDynamicArrays();
    this.prefillUserFields(); // Prefill CID and Email from localStorage
    this.loadCourses();
    this.loadCompanies();
    this.loadUserInformation(); // Load data in background, don't block UI
    console.log('✅ ngOnInit completed');
  }

  /**
   * Load user information from localStorage
   */
  private loadUserFromLocalStorage(): void {
    console.log('🔍 Loading user from localStorage...');
    const currentUser = this.authService.getCurrentUser();

    if (currentUser) {
      this.userId = currentUser.id;
      this.userCid = currentUser.cid || '';
      this.userEmail = currentUser.email || '';
      console.log('✅ Loaded user from localStorage:', {
        userId: this.userId,
        cid: this.userCid,
        email: this.userEmail,
        fullUser: currentUser
      });
    } else {
      console.error('❌ No user found in localStorage');
      this.isLoadingUserData = false;
      // Will redirect in ngOnInit
    }
  }

  /**
   * Prefill CID and Email fields with user's data from localStorage
   */
  private prefillUserFields(): void {
    // Prefill CID
    if (this.userCid) {
      this.createUserForm.patchValue({ cid: this.userCid });
      // Make CID field readonly
      this.createUserForm.get('cid')?.disable();
    }

    // Prefill Email
    if (this.userEmail) {
      this.createUserForm.patchValue({ email: this.userEmail });
      // Make Email field readonly
      this.createUserForm.get('email')?.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Set maximum birth date to today
   */
  private setMaxBirthDate(): void {
    const today = new Date();
    this.maxBirthDate = today.toISOString().split('T')[0];
  }

  /**
   * Handle birth date input with auto-formatting (dd/mm/yyyy BE)
   */
  onBirthDateInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    this.createUserForm.patchValue({ birthDate: value }, { emitEvent: false });
  }

  /**
   * Validate birth date format and convert BE to Gregorian for storage
   */
  validateBirthDate(): void {
    const birthDateValue = this.createUserForm.get('birthDate')?.value;
    
    if (!birthDateValue) {
      return;
    }

    // Check format dd/mm/yyyy
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = birthDateValue.match(datePattern);
    
    if (!match) {
      this.createUserForm.get('birthDate')?.setErrors({ invalidFormat: true });
      return;
    }

    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const yearBE = parseInt(match[3]);
    
    // Convert BE to Gregorian
    const yearCE = yearBE - 543;
    
    // Validate date ranges
    if (day < 1 || day > 31 || month < 1 || month > 12 || yearBE < 2400 || yearBE > 2569) {
      this.createUserForm.get('birthDate')?.setErrors({ invalidDate: true });
      return;
    }

    // Check if date is valid
    const testDate = new Date(yearCE, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1) {
      this.createUserForm.get('birthDate')?.setErrors({ invalidDate: true });
      return;
    }

    // Check if date is not in the future
    const today = new Date();
    if (testDate > today) {
      this.createUserForm.get('birthDate')?.setErrors({ futureDate: true });
      return;
    }

    // Store the Gregorian date in ISO format for backend (but keep BE display in form)
    this.birthDateBE = birthDateValue; // Keep BE format for display
    
    // Clear any errors
    this.createUserForm.get('birthDate')?.setErrors(null);
  }

  /**
   * Convert BE date string (dd/mm/yyyy) to Gregorian ISO format (yyyy-mm-dd)
   */
  private convertBEtoGregorian(beDateStr: string): string | null {
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = beDateStr.match(datePattern);
    
    if (!match) return null;
    
    const day = match[1];
    const month = match[2];
    const yearBE = parseInt(match[3]);
    const yearCE = yearBE - 543;
    
    return `${yearCE}-${month}-${day}`;
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.createUserForm = this.fb.group({
      // Step 1: Personal Information
      prefix: [''],
      firstNameThai: [''],
      lastNameThai: [''],
      firstNameEnglish: [''],
      lastNameEnglish: [''],
      firstName: [''],
      lastName: [''],
      email: [''],
      phone: [''],
      phoneNumber: [''],
      cid: [''],
      gender: [''],
      birthDate: [''],
      dateOfBirth: [''],
      maritalStatus: [''],
      nationality: [''],

      // Address Information
      registeredAddress: [''],
      registeredSubdistrict: [''],
      registeredDistrict: [''],
      registeredProvince: [''],
      registeredPostalCode: [''],

      address: [''],
      subdistrict: [''],
      district: [''],
      province: [''],
      postalCode: [''],

      currentAddress: [''],
      currentSubdistrict: [''],
      currentDistrict: [''],
      currentProvince: [''],
      currentPostalCode: [''],
      currentPhone: [''],
      currentFax: [''],
      currentEmail: [''],
      currentLineId: [''],
      currentFacebook: [''],

      sameAsRegistered: [false],

      // Step 2: Professional Information
      memberType: [''],
      education: [''],
      educationField: [''],
      valuationExperienceYears: [''],
      valuationExperienceMonths: [''],

      workplaceName: [''],
      businessType: [''],
      workplaceAddress: [''],
      workplacePhone: [''],
      workplaceFax: [''],
      workplaceEmail: [''],
      workplaceLineId: [''],
      workplaceFacebook: [''],
      organization: [''],
      companyId: [''],

      vatMemberType: [''],
      vatMemberExpiry: [''],
      vatId: [''],
      vatExpired: [''],
      vatCpd: [0],
      isLifetimeMember: [false],

      valuerRank: [''],
      valuerRankExpiry: [''],
      valuerId: [''],
      valuerExpired: [''],

      tvaCpd: [0],
      otherCpd: [0],
      totalCpd: [0],

      notes: [''],
      profilePicture: [''],

      // Dynamic arrays
      workHistory: this.fb.array([]),
      valuationExperience: this.fb.array([]),
      educationHistory: this.fb.array([]),
      educationBackground: this.fb.array([]),
      specialCourses: this.fb.array([]),
      vatCourses: this.fb.array([])
    });
  }


  /**
   * Load select data (licenses and ranks)
   * Note: These methods will be implemented when UserService is updated
   */
  private loadSelectData(): void {
    // TODO: Implement when UserService has getLicenseTypes and getRankTypes methods
    this.isLoadingData = false;
  }

  /**
   * Load courses from API
   * Only loads courses with courseStatusId = 3 and endDate before 1 January 2569 (2026-01-01)
   */
  private loadCourses(): void {
    this.isLoadingCourses = true;
    this.http.get<any[]>(`${environment.apiUrl}/courses`).subscribe({
      next: (courses) => {
        // Filter courses to only show those with courseStatusId = 3 and endDate before 2026-01-01
        const cutoffDate = new Date('2026-01-01');
        this.courseList = courses.filter(course => {
          if (course.courseStatusId !== 3) return false;

          // Check if endDate exists and is before cutoff date
          if (course.endDate) {
            const courseEndDate = new Date(course.endDate);
            return courseEndDate < cutoffDate;
          }

          return false; // Exclude courses without endDate
        });
        this.isLoadingCourses = false;
        console.log('📚 Loaded courses (courseStatusId = 3, endDate < 2026-01-01):', this.courseList.length, 'out of', courses.length, 'total');
      },
      error: (error) => {
        console.error('❌ Error loading courses:', error);
        this.isLoadingCourses = false;
      }
    });
  }

  /**
   * Load companies from API
   */
  private loadCompanies(): void {
    this.isLoadingCompanies = true;
    this.http.get<any>(`${environment.apiUrl}/companies?page=1&limit=100`).subscribe({
      next: (response) => {
        this.companyList = response.data || [];
        this.isLoadingCompanies = false;
        console.log('🏢 Loaded companies:', this.companyList.length);
      },
      error: (error) => {
        console.error('❌ Error loading companies:', error);
        this.isLoadingCompanies = false;
      }
    });
  }

  /**
   * Load user information from API
   */
  private loadUserInformation(): void {
    if (!this.userId) {
      console.error('❌ No userId found, cannot load user information');
      return;
    }

    console.log('📥 Loading user information in background for userId:', this.userId);
    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
    console.log('📍 API URL:', apiUrl);

    this.http.get<any>(apiUrl).subscribe({
      next: (userInfo) => {
        console.log('✅ User information loaded successfully:', userInfo);
        console.log('🔍 VAT Type from backend:', userInfo.vatType);
        console.log('🔍 VAT Member Type from backend:', userInfo.vatMemberType);

        if (userInfo && userInfo.id) {
          this.userInformationId = userInfo.id;
          this.userStatusId = userInfo.userStatusId || null;
          this.userStatusNote = userInfo.userStatusNote || '';
          console.log('📊 User status ID:', this.userStatusId);
          console.log('📝 User status note:', this.userStatusNote);
          this.populateFormWithUserData(userInfo);
          this.loadCpdHours();
          console.log('✅ Form populated with existing data');
        } else {
          console.warn('⚠️ No user information data found - form will be empty');
        }
      },
      error: (error) => {
        console.error('❌ Error loading user information:', error);

        // Handle different error types
        if (error.status === 0) {
          console.error('🔌 Network error: Cannot connect to backend server');
          console.error(`Please ensure backend is running on ${environment.apiUrl}`);
          console.warn('⚠️ User can still edit form with empty data');
        } else if (error.status === 404) {
          console.warn('⚠️ No user information found - user can create new data');
        } else if (error.status === 304) {
          console.log('ℹ️ Using cached data');
        } else {
          console.error('❌ Unexpected error:', error.status, error.message);
        }

        // Don't show alert - just log and let user continue
        console.log('ℹ️ Form is ready for editing (empty or with cached data)');
      }
    });
  }

  /**
   * Load CPD hours from API
   */
  private loadCpdHours(): void {
    if (!this.userId) {
      return;
    }

    console.log('📥 Loading CPD hours for userId:', this.userId);

    this.http.get<any[]>(`${environment.apiUrl}/cpd-hours/user/${this.userId}`).subscribe({
      next: (cpdRecords) => {
        console.log('✅ CPD hours loaded:', cpdRecords.length, 'records');
        this.populateVatCoursesFromCpdHours(cpdRecords);
      },
      error: (error) => {
        console.error('❌ Error loading CPD hours:', error);
        // Don't show error alert, just log it
      }
    });
  }

  /**
   * Populate form with user data from API
   */
  private populateFormWithUserData(userInfo: any): void {
    console.log('📝 Populating form with user data');

    try {
      // Clear existing dynamic arrays
      while (this.valuationExperience.length > 0) {
        this.valuationExperience.removeAt(0);
      }
      while (this.educationBackground.length > 0) {
        this.educationBackground.removeAt(0);
      }
      while (this.workHistory.length > 0) {
        this.workHistory.removeAt(0);
      }
      while (this.specialCourses.length > 0) {
        this.specialCourses.removeAt(0);
      }

      // Populate basic fields
      console.log('📋 Populating prefix field with value:', userInfo.titleName);
      this.createUserForm.patchValue({
        prefix: userInfo.titleName || '',
        firstName: userInfo.firstName || userInfo.firstNameThai || '',
        lastName: userInfo.lastName || userInfo.lastNameThai || '',
        firstNameThai: userInfo.firstNameThai || '',
        lastNameThai: userInfo.lastNameThai || '',
        firstNameEnglish: userInfo.firstNameEnglish || '',
        lastNameEnglish: userInfo.lastNameEnglish || '',
        email: userInfo.currentEmail || this.userEmail || '',
        phone: userInfo.phoneNumber || userInfo.currentPhone || '',
        cid: this.userCid,
        gender: userInfo.gender || '',
        profilePicture: userInfo.profileImage || userInfo.profilePicture || '',
        birthDate: userInfo.dateOfBirth ? this.formatDateForInput(userInfo.dateOfBirth) : '',
        maritalStatus: userInfo.maritalStatus || '',
        nationality: userInfo.nationality || '',

        // Registered Address
        registeredAddress: userInfo.address || '',
        registeredSubdistrict: userInfo.subdistrict || '',
        registeredDistrict: userInfo.district || '',
        registeredProvince: userInfo.province || '',
        registeredPostalCode: userInfo.postalCode || '',

        // Current Address
        currentAddress: userInfo.currentAddress || '',
        currentSubdistrict: userInfo.currentSubdistrict || '',
        currentDistrict: userInfo.currentDistrict || '',
        currentProvince: userInfo.currentProvince || '',
        currentPostalCode: userInfo.currentPostalCode || '',
        currentPhone: userInfo.currentPhone || '',
        currentFax: userInfo.currentFax || '',
        currentEmail: userInfo.currentEmail || '',
        currentLineId: userInfo.currentLineId || '',
        currentFacebook: userInfo.currentFacebook || '',

        // Professional Information
        memberType: userInfo.memberType || '',
        education: userInfo.education || '',
        educationField: userInfo.educationField || '',
        valuationExperienceYears: userInfo.valuationExperienceYears || '',
        valuationExperienceMonths: userInfo.valuationExperienceMonths || '',

        workplaceName: userInfo.workplaceName || '',
        businessType: userInfo.businessType || '',
        workplaceAddress: userInfo.workplaceAddress || '',
        workplacePhone: userInfo.workplacePhone || '',
        workplaceFax: userInfo.workplaceFax || '',
        workplaceEmail: userInfo.workplaceEmail || '',
        workplaceLineId: userInfo.workplaceLineId || '',
        workplaceFacebook: userInfo.workplaceFacebook || '',
        organization: userInfo.organization || '',
        companyId: userInfo.companyId || '',

        vatMemberType: userInfo.vatType || userInfo.vatMemberType || '',
        vatMemberExpiry: userInfo.vatExpired ? this.formatDateForInput(userInfo.vatExpired) : '',
        vatId: userInfo.vatId || '',

        valuerRank: userInfo.valuerType || '',
        valuerRankExpiry: userInfo.valuerExpired ? this.formatDateForInput(userInfo.valuerExpired) : '',
        valuerId: userInfo.valuerId || '',

        vatCpd: userInfo.vatCpd || 0,
        tvaCpd: userInfo.tvaCpd || 0,
        otherCpd: userInfo.otherCpd || 0,
        totalCpd: userInfo.totalCpd || 0,

        notes: userInfo.notes || ''
      });

      // Set selected company if companyId exists
      if (userInfo.companyId && this.companyList.length > 0) {
        this.selectedCompany = this.companyList.find(c => c.id === userInfo.companyId);
      }

      // Populate dynamic arrays
      if (userInfo.valuationExperience && Array.isArray(userInfo.valuationExperience)) {
        userInfo.valuationExperience.forEach((exp: any) => {
          const expGroup = this.fb.group({
            organization: [exp.organization || ''],
            position: [exp.position || ''],
            years: [exp.years || '']
          });
          this.valuationExperience.push(expGroup);
        });
      }

      if (userInfo.educationHistory && Array.isArray(userInfo.educationHistory)) {
        userInfo.educationHistory.forEach((edu: any) => {
          const eduGroup = this.fb.group({
            institution: [edu.Institution || edu.institution || ''],
            degree: [edu['Degree and Major'] || edu.degree || ''],
            year: [edu['Year Complete'] || edu.year || '']
          });
          this.educationBackground.push(eduGroup);
        });
      }

      if (userInfo.workHistory && Array.isArray(userInfo.workHistory)) {
        userInfo.workHistory.forEach((work: any) => {
          const workGroup = this.fb.group({
            Organization: [work.Organization || ''],
            Position: [work.Position || ''],
            Years: [work.Years || '']
          });
          this.workHistory.push(workGroup);
        });
      }

      if (userInfo.specialCourses && Array.isArray(userInfo.specialCourses)) {
        userInfo.specialCourses.forEach((course: any) => {
          const courseGroup = this.fb.group({
            courseTopic: [course.courseTopic || ''],
            organization: [course.organization || ''],
            cpdHours: [course.cpdHours || ''],
            trainingDate: [course.trainingDate || ''],
            status: [course.status || '']
          });
          this.specialCourses.push(courseGroup);
        });
      }

      // Ensure at least one row in each dynamic array
      if (this.valuationExperience.length === 0) {
        this.addValuationExperience();
      }
      if (this.educationBackground.length === 0) {
        this.addEducationBackground();
      }
      if (this.workHistory.length === 0) {
        this.addWorkHistory();
      }
      if (this.specialCourses.length === 0) {
        this.addSpecialCourse();
      }

      console.log('✅ Form populated successfully');

      // Update Buddhist Era year display if birthDate exists
      const birthDate = this.createUserForm.get('birthDate')?.value;
      if (birthDate) {
        // If birthDate is in Gregorian format (YYYY-MM-DD), convert to BE format
        if (birthDate.includes('-')) {
          const [year, month, day] = birthDate.split('-');
          const yearBE = parseInt(year) + 543;
          this.birthDateBE = `${day}/${month}/${yearBE}`;
          // Update form with BE format
          this.createUserForm.patchValue({ birthDate: this.birthDateBE }, { emitEvent: false });
        } else {
          // Already in BE format
          this.birthDateBE = birthDate;
        }
      }

      // Disable the entire form to make it view-only
      this.createUserForm.disable();

      console.log('✅ Form populated and disabled (view-only)');
    } catch (error) {
      console.error('❌ Error populating form:', error);
      this.isLoadingUserData = false;
    }
  }

  /**
   * Disable VAT Member and Valuer fields to make them read-only
   * @deprecated The entire form is now disabled
   */
  private disableVatAndValuerFields(): void {
    console.log('🔒 Disabling all fields (deprecated method called)...');
    this.createUserForm.disable();
  }

  /**
   * Populate VAT courses from CPD hours data
   */
  private populateVatCoursesFromCpdHours(cpdRecords: any[]): void {
    // Clear existing VAT courses
    while (this.vatCourses.length > 0) {
      this.vatCourses.removeAt(0);
    }

    if (cpdRecords && cpdRecords.length > 0) {
      cpdRecords.forEach((record: any) => {
        const courseGroup = this.fb.group({
          courseId: [record.courseId || ''],
          courseName: [record.sessionTitle || ''],
          cpdHours: [record.hoursStudied || 0],
          completionDate: [record.studyDate ? this.formatDateForInput(record.studyDate) : ''],
          recordId: [record.id || ''], // Store the CPD record ID
          evidenceFile: [null],
          evidenceFileName: [''],
          uploadStatus: [''],
          imageThumbnail: [record.imageThumbnail || ''] // Store existing image URL
        });
        this.vatCourses.push(courseGroup);
      });
    }

    // Ensure at least one row
    if (this.vatCourses.length === 0) {
      this.addVatCourse();
    }

    // Disable the form again to catch newly added dynamic fields
    this.createUserForm.disable();

    console.log('✅ VAT courses populated and disabled:', this.vatCourses.length, 'courses');
  }

  /**
   * Format date from ISO string to YYYY-MM-DD for input fields
   */
  private formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  /**
   * Initialize dynamic arrays with default rows
   */
  private initializeDynamicArrays(): void {
    // Only initialize if arrays are empty
    if (this.workHistory.length === 0) {
      this.addWorkHistory();
    }
    if (this.valuationExperience.length === 0) {
      this.addValuationExperience();
    }
    if (this.educationHistory.length === 0) {
      this.addEducationHistory();
    }
    if (this.educationBackground.length === 0) {
      this.addEducationBackground();
    }
    if (this.specialCourses.length === 0) {
      this.addSpecialCourse();
    }
    if (this.vatCourses.length === 0) {
      this.addVatCourse();
    }
  }

  /**
   * Setup watchers for address fields
   */
  private setupAddressWatchers(): void {
    const registeredFields = ['registeredAddress', 'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode'];

    registeredFields.forEach(field => {
      this.createUserForm.get(field)?.valueChanges.subscribe(() => {
        if (this.createUserForm.get('sameAsRegistered')?.value) {
          this.copyRegisteredToCurrent();
        }
      });
    });
  }

  /**
   * Handle same as registered checkbox change
   */
  onSameAsRegisteredChange(event: any): void {
    const isChecked = event.target.checked;

    if (isChecked) {
      this.copyRegisteredToCurrent();
      this.toggleCurrentAddressFields(false);
    } else {
      this.toggleCurrentAddressFields(true);
    }
  }

  /**
   * Handle lifetime member checkbox change
   */
  onLifetimeMemberChange(event: any): void {
    const isChecked = event.target.checked;

    if (isChecked) {
      // Disable and clear the expiry date field
      this.createUserForm.get('vatMemberExpiry')?.setValue('');
      this.createUserForm.get('vatMemberExpiry')?.disable();
      console.log('📝 Lifetime member checked - disabled expiry date field');
    } else {
      // Enable the expiry date field
      this.createUserForm.get('vatMemberExpiry')?.enable();
      console.log('📝 Lifetime member unchecked - enabled expiry date field');
    }
  }

  /**
   * Copy registered address to current address
   */
  private copyRegisteredToCurrent(): void {
    const registeredData = {
      currentAddress: this.createUserForm.get('registeredAddress')?.value,
      currentSubdistrict: this.createUserForm.get('registeredSubdistrict')?.value,
      currentDistrict: this.createUserForm.get('registeredDistrict')?.value,
      currentProvince: this.createUserForm.get('registeredProvince')?.value,
      currentPostalCode: this.createUserForm.get('registeredPostalCode')?.value
    };

    this.createUserForm.patchValue(registeredData, { emitEvent: false });
  }

  /**
   * Toggle current address fields enabled/disabled
   */
  private toggleCurrentAddressFields(enabled: boolean): void {
    const currentFields = ['currentAddress', 'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode'];

    currentFields.forEach(field => {
      const control = this.createUserForm.get(field);
      if (enabled) {
        control?.enable();
      } else {
        control?.disable();
      }
    });
  }

  // Valuation Experience Methods
  get valuationExperience(): FormArray {
    return this.createUserForm.get('valuationExperience') as FormArray;
  }

  addValuationExperience(): void {
    const experienceGroup = this.fb.group({
      organization: [''],
      position: [''],
      years: ['']
    });

    this.valuationExperience.push(experienceGroup);
  }

  removeValuationExperience(index: number): void {
    if (this.valuationExperience.length > 1) {
      this.valuationExperience.removeAt(index);
    }
  }

  // Work History Methods
  get workHistory(): FormArray {
    return this.createUserForm.get('workHistory') as FormArray;
  }

  addWorkHistory(): void {
    const workGroup = this.fb.group({
      Organization: [''],
      Position: [''],
      Years: ['']
    });

    this.workHistory.push(workGroup);
  }

  removeWorkHistory(index: number): void {
    if (this.workHistory.length > 1) {
      this.workHistory.removeAt(index);
    }
  }

  // Education History Methods
  get educationHistory(): FormArray {
    return this.createUserForm.get('educationHistory') as FormArray;
  }

  addEducationHistory(): void {
    const educationGroup = this.fb.group({
      Institution: [''],
      'Degree and Major': [''],
      'Year Complete': ['']
    });

    this.educationHistory.push(educationGroup);
  }

  removeEducationHistory(index: number): void {
    if (this.educationHistory.length > 1) {
      this.educationHistory.removeAt(index);
    }
  }

  // Education Background Methods
  get educationBackground(): FormArray {
    return this.createUserForm.get('educationBackground') as FormArray;
  }

  addEducationBackground(): void {
    const educationGroup = this.fb.group({
      institution: [''],
      degree: [''],
      year: ['']
    });

    this.educationBackground.push(educationGroup);
  }

  removeEducationBackground(index: number): void {
    if (this.educationBackground.length > 1) {
      this.educationBackground.removeAt(index);
    }
  }

  // Special Courses Methods
  get specialCourses(): FormArray {
    return this.createUserForm.get('specialCourses') as FormArray;
  }

  addSpecialCourse(): void {
    const courseGroup = this.fb.group({
      courseTopic: [''],
      organization: [''],
      cpdHours: [''],
      trainingDate: [''],
      status: ['']
    });

    this.specialCourses.push(courseGroup);
  }

  removeSpecialCourse(index: number): void {
    if (this.specialCourses.length > 1) {
      this.specialCourses.removeAt(index);
    }
  }

  // VAT Courses Methods
  get vatCourses(): FormArray {
    return this.createUserForm.get('vatCourses') as FormArray;
  }

  addVatCourse(): void {
    const courseGroup = this.fb.group({
      courseId: [''],
      courseName: [''],
      cpdHours: [''],
      completionDate: [''],
      recordId: [''],
      evidenceFile: [null],
      evidenceFileName: [''],
      uploadStatus: [''], // 'uploading', 'success', 'error'
      imageThumbnail: [''] // URL to the uploaded image
    });

    this.vatCourses.push(courseGroup);
  }

  removeVatCourse(index: number): void {
    if (this.vatCourses.length > 1) {
      this.vatCourses.removeAt(index);
    }
  }

  /**
   * View generic image url in new tab
   */
  viewImage(imageUrl: string | null | undefined): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    } else {
      alert('ไม่พบรูปภาพ');
    }
  }

  /**
   * View VAT course evidence image in new tab
   */
  viewVatCourseImage(index: number): void {
    const courseGroup = this.vatCourses.at(index) as FormGroup;
    const imageThumbnail = courseGroup.get('imageThumbnail')?.value;

    if (imageThumbnail) {
      window.open(imageThumbnail, '_blank');
    } else {
      alert('ไม่พบรูปภาพ');
    }
  }

  /**
   * Handle file selection and automatically upload for VAT course evidence
   */
  onVatCourseFileSelectAndUpload(index: number, event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('กรุณาเลือกไฟล์รูปภาพหรือ PDF เท่านั้น (JPG, PNG, GIF, PDF)');
      event.target.value = '';
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    const courseGroup = this.vatCourses.at(index) as FormGroup;

    // Store file in form control
    courseGroup.patchValue({
      evidenceFile: file,
      evidenceFileName: file.name
    });

    console.log('📎 File selected, starting automatic upload:', file.name);

    // Automatically upload the file
    this.uploadEvidenceFile(index, file);
  }

  private uploadEvidenceFile(index: number, file: File): void {
    const courseGroup = this.vatCourses.at(index) as FormGroup;

    // Set uploading status
    courseGroup.patchValue({ uploadStatus: 'uploading' });

    // Prepare FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'cpd-evidence');

    const apiUrl = `${environment.apiUrl}/upload/image`;

    console.log('📤 Uploading evidence file');
    console.log('📍 API URL:', apiUrl);
    console.log('📎 File name:', file.name);

    // POST file to upload-image endpoint
    this.http.post(apiUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Evidence uploaded successfully:', response);

        // Set success status and update imageThumbnail
        const updateData: any = { uploadStatus: 'success' };

        // Handle response format from /upload/image
        if (response && response.url) {
          updateData.imageThumbnail = response.url;
        } else if (response && response.data && response.data.url) {
          updateData.imageThumbnail = response.data.url;
        }

        courseGroup.patchValue(updateData);

        // Clear file input and success status after 3 seconds
        setTimeout(() => {
          const fileInput = document.getElementById(`vatCourseFile_${index}`) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
          courseGroup.patchValue({ uploadStatus: '' });
        }, 3000);
      },
      error: (error) => {
        console.error('❌ Error uploading evidence:', error);

        // Set error status
        courseGroup.patchValue({ uploadStatus: 'error' });

        let errorMessage = 'เกิดข้อผิดพลาดในการอัพโหลดไฟล์';
        if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);

        // Clear error status after 3 seconds
        setTimeout(() => {
          courseGroup.patchValue({ uploadStatus: '' });
        }, 3000);
      }
    });
  }



  /**
   * Handle course selection change
   */
  onCourseSelect(index: number, event: any): void {
    const courseId = event.target.value;

    // Check if this course is already selected in another row
    const isDuplicate = this.vatCourses.controls.some((control, i) => {
      if (i === index) return false; // Skip current row
      const existingCourseId = (control as FormGroup).get('courseId')?.value;
      return existingCourseId === courseId && courseId !== '';
    });

    if (isDuplicate) {
      alert('ไม่สามารถเลือกคอร์สซ้ำได้ กรุณาเลือกคอร์สอื่น');
      // Reset the dropdown to empty
      event.target.value = '';
      const courseGroup = this.vatCourses.at(index) as FormGroup;
      courseGroup.patchValue({
        courseId: '',
        courseName: '',
        cpdHours: '',
        completionDate: ''
      });
      return;
    }

    const selectedCourse = this.courseList.find(c => c.id === courseId);

    if (selectedCourse) {
      const courseGroup = this.vatCourses.at(index) as FormGroup;

      // Format endDate to YYYY-MM-DD for date input
      let formattedEndDate = '';
      if (selectedCourse.endDate) {
        const endDate = new Date(selectedCourse.endDate);
        if (!isNaN(endDate.getTime())) {
          formattedEndDate = endDate.toISOString().split('T')[0];
        }
      }

      courseGroup.patchValue({
        courseId: selectedCourse.id,
        courseName: selectedCourse.title,
        cpdHours: parseFloat(selectedCourse.duration) || 0,
        completionDate: formattedEndDate
      });
    }
  }

  /**
   * Handle company selection change
   */
  onCompanySelect(event: any): void {
    const companyId = event.target.value;
    if (companyId) {
      this.selectedCompany = this.companyList.find(c => c.id === companyId);
      console.log('🏢 Selected company:', this.selectedCompany);
    } else {
      this.selectedCompany = null;
    }
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.validateCurrentStep()) {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step <= this.currentStep || this.validateStepsUpTo(step - 1)) {
      this.currentStep = step;
    }
  }

  /**
   * Validate current step fields
   */
  private validateCurrentStep(): boolean {
    let fieldsToValidate: string[] = [];

    switch (this.currentStep) {
      case 1:
        fieldsToValidate = [
          'prefix', 'firstName', 'lastName', 'email', 'phone', 'cid', 'gender', 'birthDate',
          'registeredAddress', 'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode'
        ];

        if (!this.createUserForm.get('sameAsRegistered')?.value) {
          fieldsToValidate.push('currentAddress', 'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode');
        }
        break;
      case 2:
        fieldsToValidate = ['vatMemberType', 'vatMemberExpiry', 'valuerRank', 'valuerRankExpiry'];
        break;
    }

    let isValid = true;
    fieldsToValidate.forEach(field => {
      const control = this.createUserForm.get(field);
      if (control) {
        control.markAsTouched();
        if (control.invalid) {
          isValid = false;
        }
      }
    });

    // Validate dynamic arrays for step 2
    if (this.currentStep === 2) {
      this.valuationExperience.controls.forEach(control => {
        Object.keys((control as FormGroup).controls).forEach(key => {
          (control as FormGroup).get(key)?.markAsTouched();
        });
      });

      this.educationBackground.controls.forEach(control => {
        Object.keys((control as FormGroup).controls).forEach(key => {
          (control as FormGroup).get(key)?.markAsTouched();
        });
      });
    }

    return isValid;
  }

  /**
   * Validate all steps up to a specific step
   */
  private validateStepsUpTo(step: number): boolean {
    for (let i = 1; i <= step; i++) {
      const currentStepBackup = this.currentStep;
      this.currentStep = i;
      if (!this.validateCurrentStep()) {
        this.currentStep = currentStepBackup;
        return false;
      }
      this.currentStep = currentStepBackup;
    }
    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string): boolean {
    const field = this.createUserForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const field = this.createUserForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'กรุณากรอกข้อมูล';
    if (field.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (field.errors['pattern']) {
      if (fieldName === 'phone') return 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก';
      if (fieldName === 'cid') return 'รหัสบัตรประชาชนต้องเป็นตัวเลข 13 หลัก';
      if (fieldName.includes('PostalCode')) return 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก';
    }
    if (field.errors['minlength']) return `ต้องมีอย่างน้อย ${field.errors['minlength'].requiredLength} ตัวอักษร`;
    if (field.errors['min']) return `ค่าต้องมากกว่าหรือเท่ากับ ${field.errors['min'].min}`;
    if (field.errors['max']) return `ค่าต้องน้อยกว่าหรือเท่ากับ ${field.errors['max'].max}`;

    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Get full address string
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const address = this.createUserForm.get(`${prefix}Address`)?.value || '';
    const subdistrict = this.createUserForm.get(`${prefix}Subdistrict`)?.value || '';
    const district = this.createUserForm.get(`${prefix}District`)?.value || '';
    const province = this.createUserForm.get(`${prefix}Province`)?.value || '';
    const postalCode = this.createUserForm.get(`${prefix}PostalCode`)?.value || '';

    if (!address && !subdistrict && !district && !province && !postalCode) {
      return '-';
    }

    return `${address} ต.${subdistrict} อ.${district} จ.${province} ${postalCode}`;
  }

  /**
   * Convert date string to ISO 8601 format
   * Handles both Buddhist Era (dd/mm/yyyy) and Gregorian (YYYY-MM-DD) formats
   */
  private convertToISO8601(dateString: string): string {
    if (!dateString) return '';

    // If already in ISO format, return as is
    if (dateString.includes('T')) return dateString;

    // Check if it's Buddhist Era format (dd/mm/yyyy)
    if (dateString.includes('/')) {
      const gregorianDate = this.convertBEtoGregorian(dateString);
      if (!gregorianDate) return '';
      dateString = gregorianDate;
    }

    // Convert YYYY-MM-DD to ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }

  /**
   * Update CPD hours data to API as array
   * Note: This deletes existing CPD records and creates new ones
   */
  private updateCpdHours(vatCourses: any[]): void {
    if (!vatCourses || vatCourses.length === 0) {
      console.log('ℹ️ No VAT courses to update');
      return;
    }

    // Filter out empty courses (no courseId selected)
    const validCourses = vatCourses.filter(course => course.courseId && course.cpdHours);

    if (validCourses.length === 0) {
      console.log('ℹ️ No valid VAT courses to update');
      return;
    }

    console.log('📤 Updating CPD hours for', validCourses.length, 'courses');

    // First, delete existing CPD records for this user
    this.http.delete(`${environment.apiUrl}/cpd-hours/user/${this.userId}`).subscribe({
      next: () => {
        console.log('✅ Existing CPD records deleted');

        // Then create new CPD records
        const cpdRecords = validCourses.map((course) => {
          return {
            userId: this.userId,
            courseId: course.courseId,
            hoursStudied: parseFloat(course.cpdHours) || 0,
            studyDate: course.completionDate || new Date().toISOString().split('T')[0],
            year: course.completionDate ? new Date(course.completionDate).getFullYear() : new Date().getFullYear(),
            sessionTitle: course.courseName || '',
            notes: `Course completed: ${course.courseName}`,
            isVerified: false,
            imageThumbnail: course.imageThumbnail || ''
          };
        });

        console.log('📤 Creating new CPD hour records:', cpdRecords);

        this.http.post(`${environment.apiUrl}/cpd-hours`, cpdRecords).subscribe({
          next: (response) => {
            console.log(`✅ All ${cpdRecords.length} CPD hour records created successfully:`, response);
          },
          error: (error) => {
            console.error('❌ Error creating CPD hour records:', error);
            console.error('Error details:', error.error);
          }
        });
      },
      error: (error) => {
        console.error('❌ Error deleting existing CPD records:', error);
        // Continue anyway - maybe there were no existing records
      }
    });
  }

  /**
   * Debug: Show JSON payload that will be sent to backend
   */
  debugShowPayload(): void {
    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      return;
    }

    const formData = this.createUserForm.getRawValue();

    // Convert dates to ISO 8601 format
    const dateOfBirth = this.convertToISO8601(formData.dateOfBirth || formData.birthDate || '');
    const vatExpired = this.convertToISO8601(formData.vatExpired || formData.vatMemberExpiry || '');
    const valuerExpired = this.convertToISO8601(formData.valuerExpired || formData.valuerRankExpiry || '');

    // Prepare data for API according to the required format
    const userInformationData: any = {
      firstNameThai: formData.firstNameThai || formData.firstName || '',
      lastNameThai: formData.lastNameThai || formData.lastName || '',
      firstNameEnglish: formData.firstNameEnglish || '',
      lastNameEnglish: formData.lastNameEnglish || '',
      memberType: formData.memberType || '',
      education: formData.education || '',
      educationField: formData.educationField || '',
      valuationExperienceYears: formData.valuationExperienceYears || '0',
      valuationExperienceMonths: formData.valuationExperienceMonths || '0',
      maritalStatus: formData.maritalStatus || '',
      nationality: formData.nationality || '',
      workplaceName: formData.workplaceName || '',
      businessType: formData.businessType || '',
      workplaceAddress: formData.workplaceAddress || '',
      workplacePhone: formData.workplacePhone || '',
      workplaceFax: formData.workplaceFax || '',
      workplaceEmail: formData.workplaceEmail || '',
      workplaceLineId: formData.workplaceLineId || '',
      workplaceFacebook: formData.workplaceFacebook || '',
      currentAddress: formData.currentAddress || '',
      currentSubdistrict: formData.currentSubdistrict || '',
      currentDistrict: formData.currentDistrict || '',
      currentProvince: formData.currentProvince || '',
      currentPostalCode: formData.currentPostalCode || '',
      currentPhone: formData.currentPhone || formData.phone || '',
      currentFax: formData.currentFax || '',
      currentEmail: formData.currentEmail || formData.email || '',
      currentLineId: formData.currentLineId || '',
      currentFacebook: formData.currentFacebook || '',
      workHistory: formData.workHistory || [],
      valuationExperience: formData.valuationExperience || [],
      educationHistory: (formData.educationBackground || []).map((edu: any) => ({
        'Institution': edu.institution || '',
        'Degree and Major': edu.degree || '',
        'Year Complete': edu.year || ''
      })),
      specialCourses: formData.specialCourses || [],
      notes: formData.notes || '',
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      phoneNumber: formData.phoneNumber || formData.phone || '',
      address: formData.address || formData.registeredAddress || '',
      subdistrict: formData.subdistrict || formData.registeredSubdistrict || '',
      district: formData.district || formData.registeredDistrict || '',
      province: formData.province || formData.registeredProvince || '',
      postalCode: formData.postalCode || formData.registeredPostalCode || '',
      gender: formData.gender || '',
      profilePicture: formData.profilePicture || '',
      profileImage: formData.profilePicture || '',
      organization: formData.organization || formData.workplaceName || '',
      companyId: formData.companyId || '',
      vatId: formData.vatId || '',
      vatType: formData.vatMemberType || '',
      valuerId: formData.valuerId || '',
      valuerType: formData.valuerRank || '',
      vatCpd: parseFloat(formData.vatCpd) || 0,
      tvaCpd: parseFloat(formData.tvaCpd) || 0,
      otherCpd: parseFloat(formData.otherCpd) || 0,
      totalCpd: parseFloat(formData.totalCpd) || 0
    };

    // Only include date fields if they have valid values
    if (dateOfBirth) {
      userInformationData.dateOfBirth = dateOfBirth;
    }
    if (vatExpired) {
      userInformationData.vatExpired = vatExpired;
    }
    if (valuerExpired) {
      userInformationData.valuerExpired = valuerExpired;
    }

    // Prepare API URL and payload
    let apiUrl = `${environment.apiUrl}/user-information/${this.userInformationId}`;
    let payload: any = userInformationData;

    // If user is rejected (status 3), change status to pending (2) and use different endpoint
    if (this.userStatusId === 3) {
      apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
      payload = {
        ...userInformationData,
        userStatusId: 2
      };
    }

    // Prepare CPD hours data
    const validCourses = (formData.vatCourses || []).filter((course: any) => course.courseId && course.cpdHours);
    const cpdRecords = validCourses.map((course: any) => {
      return {
        userId: this.userId,
        courseId: course.courseId,
        hoursStudied: parseFloat(course.cpdHours) || 0,
        studyDate: course.completionDate || new Date().toISOString().split('T')[0],
        year: course.completionDate ? new Date(course.completionDate).getFullYear() : new Date().getFullYear(),
        sessionTitle: course.courseName || '',
        notes: `Course completed: ${course.courseName}`,
        isVerified: false
      };
    });

    // Create debug output
    const debugOutput = {
      apiUrl: apiUrl,
      method: 'PATCH',
      userInformationPayload: payload,
      cpdHoursPayload: {
        method: 'POST',
        url: `${environment.apiUrl}/cpd-hours`,
        data: cpdRecords,
        note: 'CPD records will be deleted first, then created'
      }
    };

    // Display in console
    console.log('🐛 DEBUG - JSON Payload:', debugOutput);
    console.log('🐛 DEBUG - User Information Payload (formatted):', JSON.stringify(payload, null, 2));
    console.log('🐛 DEBUG - CPD Hours Payload (formatted):', JSON.stringify(cpdRecords, null, 2));

    // Prepare full JSON for modal display
    this.debugJsonData = JSON.stringify(debugOutput, null, 2);

    // Show debug modal
    this.showDebugModal = true;
  }

  /**
   * Close debug modal
   */
  closeDebugModal(): void {
    this.showDebugModal = false;
  }

  /**
   * Copy debug JSON to clipboard
   */
  copyDebugJson(): void {
    navigator.clipboard.writeText(this.debugJsonData).then(() => {
      alert('✅ คัดลอก JSON ไปยัง Clipboard แล้ว!');
    }).catch(err => {
      console.error('❌ Failed to copy to clipboard:', err);
      alert('⚠️ ไม่สามารถคัดลอกไปยัง Clipboard ได้');
    });
  }

  /**
   * Open contact admin modal
   */
  openContactModal(): void {
    this.showContactModal = true;
    this.contactMessage = '';
    this.clearContactAttachment();
  }

  /**
   * Close contact admin modal
   */
  closeContactModal(): void {
    this.showContactModal = false;
    this.contactMessage = '';
    this.clearContactAttachment();
  }

  /**
   * เลือกไฟล์แนบประกอบคำขอ รับเฉพาะ JPEG/JPG/PNG/PDF และไม่เกิน 5MB
   * อัปโหลดทันทีเพื่อให้ได้ URL ไว้ส่งไปพร้อม ticket
   */
  onContactAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;

    // เคลียร์ค่าใน input เสมอ ผู้ใช้จะได้เลือกไฟล์เดิมซ้ำได้หลังลบออก
    input.value = '';

    if (!file) {
      return;
    }

    const isAllowedType =
      this.contactAttachmentMimeTypes.includes(file.type.toLowerCase()) ||
      this.contactAttachmentExtensions.test(file.name);

    if (!isAllowedType) {
      this.clearContactAttachment();
      this.contactAttachmentError = 'รองรับเฉพาะไฟล์ JPEG, JPG, PNG และ PDF เท่านั้น';
      this.cdr.detectChanges();
      return;
    }

    if (file.size > this.contactAttachmentMaxSize) {
      this.clearContactAttachment();
      this.contactAttachmentError = `ไฟล์มีขนาด ${this.formatFileSize(file.size)} ใหญ่เกินไป กรุณาเลือกไฟล์ที่ไม่เกิน 25MB`;
      this.cdr.detectChanges();
      return;
    }

    this.contactAttachment = file;
    this.contactAttachmentUrl = null;
    this.contactAttachmentError = '';
    this.isUploadingAttachment = true;
    this.cdr.detectChanges();

    const uploadData = new FormData();
    uploadData.append('file', file, file.name);
    uploadData.append('folder', 'ticket-user-information');

    this.http.post<any>(`${environment.apiUrl}/upload/image`, uploadData).subscribe({
      next: (response) => {
        this.contactAttachmentUrl = response?.url || response?.data?.url || '';
        this.isUploadingAttachment = false;

        if (!this.contactAttachmentUrl) {
          this.contactAttachment = null;
          this.contactAttachmentError = 'อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง';
        }

        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error uploading attachment:', error);
        this.contactAttachment = null;
        this.contactAttachmentUrl = null;
        this.isUploadingAttachment = false;
        this.contactAttachmentError =
          error.error?.message || 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์ กรุณาลองใหม่อีกครั้ง';
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * ลบไฟล์แนบที่เลือกไว้
   */
  removeContactAttachment(): void {
    this.clearContactAttachment();
    this.cdr.detectChanges();
  }

  private clearContactAttachment(): void {
    this.contactAttachment = null;
    this.contactAttachmentUrl = null;
    this.contactAttachmentError = '';
    this.isUploadingAttachment = false;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  /**
   * Send contact request to create a ticket for user information update
   * POST to /ticket-user-information endpoint
   */
  sendContactEmail(): void {
    if (!this.contactMessage || this.contactMessage.trim() === '') {
      alert('กรุณากรอกข้อความก่อนส่ง');
      return;
    }

    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      return;
    }

    if (this.isUploadingAttachment) {
      alert('กำลังอัปโหลดไฟล์แนบ กรุณารอสักครู่');
      return;
    }

    // Show loading state
    this.isSubmitting = true;

    // Prepare ticket data according to API requirements
    const ticketData: {
      detail: string;
      userId: string;
      emailStatus: boolean;
      attachment?: string;
    } = {
      detail: this.contactMessage.trim(),
      userId: this.userId,
      emailStatus: true
    };

    if (this.contactAttachmentUrl) {
      ticketData.attachment = this.contactAttachmentUrl;
    }

    const apiUrl = `${environment.apiUrl}/ticket-user-information`;

    console.log('📤 Sending ticket request:', ticketData);
    console.log('📍 API URL:', apiUrl);

    // POST ticket data to API
    this.http.post(apiUrl, ticketData).subscribe({
      next: (response) => {
        console.log('✅ Ticket created successfully:', response);
        alert('ส่งข้อมูลเรียบร้อยแล้ว เจ้าหน้าที่จะติดต่อกลับทางอีเมล์ของคุณ');
        this.closeContactModal();
        this.isSubmitting = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error creating ticket:', error);
        this.isSubmitting = false;
        this.cdr.detectChanges();

        let errorMessage = 'เกิดข้อผิดพลาดในการส่งข้อมูล';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 500) {
          errorMessage = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์';
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}\nกรุณาลองใหม่อีกครั้ง`);
      }
    });
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      this.router.navigate(['/login']);
      return;
    }

    this.isSubmitting = true;

    const formData = this.createUserForm.getRawValue();

    // Convert dates to ISO 8601 format
    const dateOfBirth = this.convertToISO8601(formData.dateOfBirth || formData.birthDate || '');
    const vatExpired = this.convertToISO8601(formData.vatExpired || formData.vatMemberExpiry || '');
    const valuerExpired = this.convertToISO8601(formData.valuerExpired || formData.valuerRankExpiry || '');

    // Prepare data for API according to the required format
    // Note: userId is NOT included in PATCH request body (it's in the URL path)
    const userInformationData: any = {
      titleName: formData.prefix || '',
      firstNameThai: formData.firstNameThai || formData.firstName || '',
      lastNameThai: formData.lastNameThai || formData.lastName || '',
      firstNameEnglish: formData.firstNameEnglish || '',
      lastNameEnglish: formData.lastNameEnglish || '',
      memberType: formData.memberType || '',
      education: formData.education || '',
      educationField: formData.educationField || '',
      valuationExperienceYears: formData.valuationExperienceYears || '0',
      valuationExperienceMonths: formData.valuationExperienceMonths || '0',
      maritalStatus: formData.maritalStatus || '',
      nationality: formData.nationality || '',
      workplaceName: formData.workplaceName || '',
      businessType: formData.businessType || '',
      workplaceAddress: formData.workplaceAddress || '',
      workplacePhone: formData.workplacePhone || '',
      workplaceFax: formData.workplaceFax || '',
      workplaceEmail: formData.workplaceEmail || '',
      workplaceLineId: formData.workplaceLineId || '',
      workplaceFacebook: formData.workplaceFacebook || '',
      currentAddress: formData.currentAddress || '',
      currentPhone: formData.currentPhone || formData.phone || '',
      currentFax: formData.currentFax || '',
      currentEmail: formData.currentEmail || formData.email || '',
      currentLineId: formData.currentLineId || '',
      currentFacebook: formData.currentFacebook || '',
      workHistory: formData.workHistory || [],
      valuationExperience: formData.valuationExperience || [],
      educationHistory: (formData.educationBackground || []).map((edu: any) => ({
        'Institution': edu.institution || '',
        'Degree and Major': edu.degree || '',
        'Year Complete': edu.year || ''
      })),
      specialCourses: formData.specialCourses || [],
      notes: formData.notes || '',
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      phoneNumber: formData.phoneNumber || formData.phone || '',
      address: formData.address || formData.registeredAddress || '',
      subdistrict: formData.subdistrict || formData.registeredSubdistrict || '',
      district: formData.district || formData.registeredDistrict || '',
      province: formData.province || formData.registeredProvince || '',
      postalCode: formData.postalCode || formData.registeredPostalCode || '',
      gender: formData.gender || '',
      profilePicture: formData.profilePicture || '',
      profileImage: formData.profilePicture || '',
      organization: formData.organization || formData.workplaceName || '',
      companyId: formData.companyId || '',
      vatId: formData.vatId || '',
      vatType: formData.vatMemberType || '',
      valuerId: formData.valuerId || '',
      valuerType: formData.valuerRank || '',
      vatCpd: parseFloat(formData.vatCpd) || 0,
      tvaCpd: parseFloat(formData.tvaCpd) || 0,
      otherCpd: parseFloat(formData.otherCpd) || 0,
      totalCpd: parseFloat(formData.totalCpd) || 0
    };

    // Only include date fields if they have valid values
    if (dateOfBirth) {
      userInformationData.dateOfBirth = dateOfBirth;
    }
    if (vatExpired) {
      userInformationData.vatExpired = vatExpired;
    }
    if (valuerExpired) {
      userInformationData.valuerExpired = valuerExpired;
    }

    console.log('📤 Updating user information:', userInformationData);

    // Prepare API URL and payload
    let apiUrl = `${environment.apiUrl}/user-information/${this.userInformationId}`;
    let payload: any = userInformationData;

    // If user is rejected (status 3), change status to pending (2) and use different endpoint
    if (this.userStatusId === 3) {
      console.log('🔄 User is rejected (status 3), changing to pending (status 2)');
      apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
      payload = {
        ...userInformationData,
        userStatusId: 2
      };
    }

    console.log('📍 API URL:', apiUrl);
    console.log('📦 Payload:', payload);

    // Call API to update user information using PATCH
    this.http.patch(apiUrl, payload).subscribe({
      next: (response) => {
        console.log('✅ User information updated successfully:', response);

        // Update CPD hours data after user information is updated
        this.updateCpdHours(formData.vatCourses);

        this.isSubmitting = false;
        alert('อัพเดทข้อมูลสำเร็จ!');
        // Redirect to user dashboard
        this.router.navigate(['/user/dashboard']);
      },
      error: (error) => {
        console.error('❌ Error updating user information:', error);
        this.isSubmitting = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 500) {
          errorMessage = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์';
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Update personal information only
   */
  updatePersonalInfo(): void {
    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      return;
    }

    // Validate personal information fields
    const personalFields = ['prefix', 'firstName', 'lastName', 'email', 'phone', 'gender', 'birthDate'];
    let isValid = true;

    personalFields.forEach(field => {
      const control = this.createUserForm.get(field);
      if (control) {
        control.markAsTouched();
        if (!control.value || (typeof control.value === 'string' && control.value.trim() === '')) {
          isValid = false;
        }
      }
    });

    if (!isValid) {
      alert('กรุณากรอกข้อมูลส่วนตัวให้ครบถ้วน');
      return;
    }

    this.isSubmitting = true;

    const formData = this.createUserForm.getRawValue();

    // Convert birthDate to ISO 8601 format
    const dateOfBirth = this.convertToISO8601(formData.birthDate || '');

    // Prepare personal information data only
    const personalData: any = {
      titleName: formData.prefix || '',
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      firstNameThai: formData.firstName || '',
      lastNameThai: formData.lastName || '',
      phoneNumber: formData.phone || '',
      gender: formData.gender || '',
      companyId: formData.companyId || ''
    };

    // Only include dateOfBirth if it has a valid value
    if (dateOfBirth) {
      personalData.dateOfBirth = dateOfBirth;
    }

    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;

    console.log('📤 Updating personal information:', personalData);
    console.log('📍 API URL:', apiUrl);

    // Call API to update personal information using PATCH
    this.http.patch(apiUrl, personalData).subscribe({
      next: (response) => {
        console.log('✅ Personal information updated successfully:', response);
        this.isSubmitting = false;
        alert('อัพเดทข้อมูลส่วนตัวสำเร็จ!');
      },
      error: (error) => {
        console.error('❌ Error updating personal information:', error);
        this.isSubmitting = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 500) {
          errorMessage = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์';
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Update registered address only
   */
  updateRegisteredAddress(): void {
    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      return;
    }

    // Validate registered address fields
    const registeredFields = ['registeredAddress', 'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode'];
    let isValid = true;

    registeredFields.forEach(field => {
      const control = this.createUserForm.get(field);
      if (control) {
        control.markAsTouched();
        if (!control.value || control.value.trim() === '') {
          isValid = false;
        }
      }
    });

    if (!isValid) {
      alert('กรุณากรอกข้อมูลที่อยู่ตามทะเบียนบ้านให้ครบถ้วน');
      return;
    }

    this.isSubmitting = true;

    const formData = this.createUserForm.getRawValue();

    // Prepare registered address data only
    const addressData = {
      titleName: formData.prefix || '',
      address: formData.registeredAddress || '',
      subdistrict: formData.registeredSubdistrict || '',
      district: formData.registeredDistrict || '',
      province: formData.registeredProvince || '',
      postalCode: formData.registeredPostalCode || ''
    };

    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;

    console.log('📤 Updating registered address:', addressData);
    console.log('📍 API URL:', apiUrl);

    // Call API to update registered address using PATCH
    this.http.patch(apiUrl, addressData).subscribe({
      next: (response) => {
        console.log('✅ Registered address updated successfully:', response);
        this.isSubmitting = false;
        alert('อัพเดทที่อยู่ตามทะเบียนบ้านสำเร็จ!');
      },
      error: (error) => {
        console.error('❌ Error updating registered address:', error);
        this.isSubmitting = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 500) {
          errorMessage = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์';
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Update current address only
   */
  updateCurrentAddress(): void {
    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      return;
    }

    // Validate current address fields
    const currentFields = ['currentAddress', 'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode'];
    let isValid = true;

    currentFields.forEach(field => {
      const control = this.createUserForm.get(field);
      if (control) {
        control.markAsTouched();
        if (!control.value || control.value.trim() === '') {
          isValid = false;
        }
      }
    });

    if (!isValid) {
      alert('กรุณากรอกข้อมูลที่อยู่ปัจจุบันให้ครบถ้วน');
      return;
    }

    this.isSubmitting = true;

    const formData = this.createUserForm.getRawValue();

    // Prepare current address data only
    const addressData = {
      titleName: formData.prefix || '',
      currentAddress: formData.currentAddress || '',
      currentSubdistrict: formData.currentSubdistrict || '',
      currentDistrict: formData.currentDistrict || '',
      currentProvince: formData.currentProvince || '',
      currentPostalCode: formData.currentPostalCode || ''
    };

    const apiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;

    console.log('📤 Updating current address:', addressData);
    console.log('📍 API URL:', apiUrl);

    // Call API to update current address using PATCH
    this.http.patch(apiUrl, addressData).subscribe({
      next: (response) => {
        console.log('✅ Current address updated successfully:', response);
        this.isSubmitting = false;
        alert('อัพเดทที่อยู่ปัจจุบันสำเร็จ!');
      },
      error: (error) => {
        console.error('❌ Error updating current address:', error);
        this.isSubmitting = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 500) {
          errorMessage = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์';
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Cancel and go back to login
   */
  cancel(): void {
    if (confirm('คุณต้องการยกเลิกการแก้ไขข้อมูลหรือไม่?')) {
      this.router.navigate(['/login']);
    }
  }
}
